// src/contexts/TeacherAvailabilityContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";

import { useLessons } from "./LessonsContext";

/** UI labels only */
const defaultDaysOfWeek = ["א", "ב", "ג", "ד", "ה", "ו", "שבת"]; // 0=Sun..6=Sat
const defaultTimeSlots = [
  { start: "8:00", end: "8:50" },
  { start: "9:00", end: "9:50" },
  { start: "10:00", end: "10:50" },
  { start: "11:00", end: "11:50" },
  { start: "12:00", end: "12:50" },
  { start: "13:00", end: "13:50" },
  { start: "14:00", end: "14:50" },
  { start: "15:00", end: "15:50" },
  { start: "16:00", end: "16:50" },
  { start: "17:00", end: "17:50" },
  { start: "18:00", end: "18:50" },
  { start: "19:00", end: "19:50" },
];

const makeEmptyGrid = () =>
  Array(defaultDaysOfWeek.length)
    .fill(null)
    .map(() => Array(defaultTimeSlots.length).fill(false));

const TeacherAvailabilityContext = createContext(null);

/* ---------------- utils ---------------- */
const MIN_BOOKING_NOTICE_MINUTES = 5 * 60;
const pad2 = (n) => String(n).padStart(2, "0");

const formatDateStr = (date) => {
  if (!date) return "";
  if (typeof date === "string") return date;
  try {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
      date.getDate()
    )}`;
  } catch {
    return "";
  }
};

const hmToMinutes = (hhmm) => {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

// Canonicalize any time-ish value to "HH:00"
const canonHourStr = (t) => {
  if (!t) return "00:00";
  const h = parseInt(String(t).split(":")[0], 10);
  const hh = Number.isFinite(h) ? h : 0;
  return `${pad2(hh)}:00`;
};

const normalizeTimeArray = (arr) =>
  Array.from(new Set((arr || []).map(canonHourStr))).sort((a, b) =>
    a.localeCompare(b)
  );

const normalizeAvailabilityMap = (map) => {
  const out = {};
  if (map && typeof map === "object") {
    for (const [date, times] of Object.entries(map)) {
      out[date] = normalizeTimeArray(times);
    }
  }
  return out;
};


const filterAvailabilityMapByNotice = (availabilityMap, nowMs) => {
  if (!availabilityMap || typeof availabilityMap !== "object") return {};

  const minAllowedMs =
    Number(nowMs || 0) + MIN_BOOKING_NOTICE_MINUTES * 60 * 1000;

  const out = {};

  for (const [dateKey, times] of Object.entries(availabilityMap)) {
    const filtered = normalizeTimeArray(times).filter((t) => {
      const slotDateTime = new Date(`${dateKey}T${t}:00`);
      return slotDateTime.getTime() >= minAllowedMs;
    });

    if (filtered.length) {
      out[dateKey] = filtered;
    }
  }

  return out;
};

/* ---------- demo weekly grid (deterministic per owner+month) ---------- */
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const hashStr = (str) => {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};
const mulberry32 = (a) => {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const generateDemoWeeklyGrid = (monthDate, ownerKey) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth() + 1;
  const seed = hashStr(`${ownerKey || "unknown"}-${year}-${month}`);
  const rnd = mulberry32(seed);

  const grid = makeEmptyGrid();
  const markBlock = (dayIdx, startIdx, len) => {
    for (let i = 0; i < len; i++) {
      const idx = startIdx + i;
      if (idx >= 0 && idx < defaultTimeSlots.length) grid[dayIdx][idx] = true;
    }
  };

  for (let day = 0; day < 7; day++) {
    if (day <= 4) {
      const blocks = 1 + Math.floor(rnd() * 2);
      for (let b = 0; b < blocks; b++) {
        const startHour = clamp(9 + Math.floor(rnd() * 9), 8, 17);
        const startIdx = clamp(startHour - 8, 0, defaultTimeSlots.length - 2);
        const len = 2 + Math.floor(rnd() * 3);
        markBlock(day, startIdx, len);
      }
    } else if (day === 5) {
      if (rnd() < 0.6) {
        const startHour = clamp(9 + Math.floor(rnd() * 2), 8, 11);
        const startIdx = clamp(startHour - 8, 0, defaultTimeSlots.length - 2);
        const len = 2 + Math.floor(rnd() * 2);
        markBlock(day, startIdx, len);
      }
    } else {
      if (rnd() < 0.1) {
        const startHour = clamp(17 + Math.floor(rnd() * 2), 16, 18);
        const startIdx = clamp(startHour - 8, 0, defaultTimeSlots.length - 1);
        const len = 1 + Math.floor(rnd() * 2);
        markBlock(day, startIdx, len);
      }
    }
  }
  return grid;
};

/* ---------- API base & auth ---------- */
const ENV_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  "";
const CLEAN_ENV_BASE = ENV_BASE.replace(/\/+$/, "");
const FALLBACK_BASE = "https://www.doroosonline.co.il";
const API_BASE = CLEAN_ENV_BASE || FALLBACK_BASE;
const url = (path) => `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

const TOKEN_KEYS = ["token", "jwt", "JWT", "accessToken", "authToken"];
const getToken = () => {
  try {
    for (const k of TOKEN_KEYS) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k);
      if (v) return v;
    }
  } catch {}
  return null;
};

const withTimeout = (ms, signal) =>
  new Promise((_, reject) => {
    const id = setTimeout(() => reject(new Error("Request timeout")), ms);
    signal?.addEventListener("abort", () => clearTimeout(id));
  });

const safeParseJson = (txt) => {
  if (!txt) return null;
  try {
    return JSON.parse(txt);
  } catch {
    return null;
  }
};

const apiFetch = async (path, { timeout = 12000, headers = {}, signal } = {}) => {
  const token = getToken();
  const controller = !signal ? new AbortController() : null;
  const usedSignal = signal || controller.signal;

  const fetchPromise = fetch(url(path), {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    mode: "cors",
    cache: "no-store",
    credentials: "omit",
    signal: usedSignal,
  }).then(async (res) => {
    const txt = await res.text().catch(() => "");
    const data = safeParseJson(txt);
    if (!res.ok) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
    return data;
  });

  return await Promise.race([fetchPromise, withTimeout(timeout, usedSignal)]);
};

/* ----- endpoints wrappers ----- */
// legacy weekly + lessons (fallback path)
const fetchTeacherWeeklyGrid = async (ownerKey, { signal } = {}) => {
  if (!ownerKey) return null;
  return await apiFetch(`/api/teachers/${encodeURIComponent(ownerKey)}/availability`, { signal });
};

const fetchTeacherLessonsForMonth = async (ownerKey, monthDate, { signal } = {}) => {
  if (!ownerKey || !monthDate) return [];
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const from = `${year}-${pad2(month + 1)}-01`;
  const to = `${year}-${pad2(month + 1)}-${pad2(new Date(year, month + 1, 0).getDate())}`;
  return await apiFetch(
    `/api/teachers/${encodeURIComponent(ownerKey)}/lessons?from=${from}&to=${to}`,
    { signal }
  );
};

// NEW: server-side monthly availability (preferred path)
const fetchTeacherMonthlyAvailability = async (ownerIdNum, monthDate, { signal } = {}) => {
  if (!ownerIdNum || !monthDate) return null;
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth() + 1;
  return await apiFetch(`/api/availability/${ownerIdNum}/month?year=${y}&month=${m}`, { signal });
};

const asNumericId = (ownerKey) => {
  if (ownerKey === null || ownerKey === undefined) return null;
  const n = Number(ownerKey);
  return Number.isFinite(n) && String(n) === String(ownerKey) ? n : null;
};

/* ---------- client-side compute (fallback if server monthly unavailable) ---------- */
const normHour = (hhmm) => canonHourStr(hhmm);

const isSameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isBlockingStatus = (s) => !["CANCELLED", "NOT_APPROVED"].includes(s || "");

const computeAvailableSlotsForMonth = ({
  monthDate,
  savedAvailability,
  timeSlots,
  lessons,
  nowMs,
}) => {

  if (!monthDate || !Array.isArray(savedAvailability) || !Array.isArray(timeSlots)) return {};
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const now = new Date(nowMs ?? Date.now());
  const todayKey = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const lessonsByDate = (lessons || []).reduce((acc, l) => {
    if (!l?.date || !l?.startTime || !l?.endTime) return acc;
    if (!isBlockingStatus(l.status)) return acc;
    (acc[l.date] ||= []).push(l);
    return acc;
  }, {});

  const out = {};
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month, day);
    if (d < todayKey) continue;

    const dateKey = formatDateStr(d);
    const weekDayIdx = d.getDay(); // 0..6 Sun..Sat
    const weeklyRow = savedAvailability[weekDayIdx] || [];

    let base = timeSlots
      .map((slot, idx) => ({ slot, idx }))
      .filter(({ idx }) => weeklyRow[idx])
      .map(({ slot }) => normHour(slot.start));

    const minAllowedMs = now.getTime() + MIN_BOOKING_NOTICE_MINUTES * 60 * 1000;

    base = base.filter((t) => {
      const slotDateTime = new Date(`${dateKey}T${t}:00`);
      return slotDateTime.getTime() >= minAllowedMs;
    });

    const dayLessons = lessonsByDate[dateKey] || [];
    const filtered = base.filter((slotStartStr) => {
      const slotStart = hmToMinutes(slotStartStr);
      const slotEnd = slotStart + 60;
      return !dayLessons.some((l) => {
        const ls = hmToMinutes(l.startTime);
        const le = hmToMinutes(l.endTime);
        return slotStart < le && ls < slotEnd;
      });
    });

    if (filtered.length) out[dateKey] = normalizeTimeArray(filtered);
  }
  return out;
};

/**
 * Provider
 */
export const TeacherAvailabilityProvider = ({ ownerKey, children }) => {
  const [savedAvailability, setSavedAvailability] = useState(makeEmptyGrid()); // weekly, for fallback
  const [hasRealSaved, setHasRealSaved] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [availableSlots, setAvailableSlots] = useState({});
  const [monthLessons, setMonthLessons] = useState([]);
  const [serverOffsetMs, setServerOffsetMs] = useState(0);

  const {getServerTime} = useLessons();

  const lastFetchKeyRef = useRef("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const serverNowMs = await getServerTime();

        if (!cancelled) {
          setServerOffsetMs(serverNowMs - Date.now());
        }
      } catch (err) {
        console.error("[TeacherAvailability] Failed to fetch server time:", err);

        if (!cancelled) {
          setServerOffsetMs(0);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const monthKey = useMemo(() => {
    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth() + 1;
    return `${ownerKey || "unknown"}:${y}-${pad2(m)}`;
  }, [ownerKey, currentMonth]);

  // Weekly grid (for fallback and demo)
  const fetchGrid = useCallback(
    async (signal) => {
      try {
        const grid = await fetchTeacherWeeklyGrid(ownerKey, { signal });
        if (grid && Array.isArray(grid) && grid.length === defaultDaysOfWeek.length) {
          setSavedAvailability(grid);
          setHasRealSaved(true);
        } else {
          setHasRealSaved(false);
          setSavedAvailability(generateDemoWeeklyGrid(currentMonth, ownerKey));
        }
      } catch {
        setHasRealSaved(false);
        setSavedAvailability(generateDemoWeeklyGrid(currentMonth, ownerKey));
      }
    },
    [ownerKey, currentMonth]
  );

  // Lessons (for fallback)
  const fetchMonthLessons = useCallback(
    async (signal) => {
      try {
        const lessons = await fetchTeacherLessonsForMonth(ownerKey, currentMonth, { signal });
        setMonthLessons(Array.isArray(lessons) ? lessons : []);
      } catch {
        setMonthLessons([]);
      }
    },
    [ownerKey, currentMonth]
  );

  // Preferred: server monthly availability
  const fetchMonthlyAvailability = useCallback(
    async (signal) => {
      const ownerIdNum = asNumericId(ownerKey);
      if (!ownerIdNum) return null; // cannot use server route without numeric id
      try {
        const data = await fetchTeacherMonthlyAvailability(ownerIdNum, currentMonth, { signal });

        // shape: { ok: true, availability: { "YYYY-MM-DD": ["HH:00", ...] } }
        if (data && data.ok && typeof data.availability === "object") {
          return normalizeAvailabilityMap(data.availability || {});
        }

        // if backend returns raw map without ok wrapper:
        if (data && !data.ok && typeof data === "object") return normalizeAvailabilityMap(data);

        return null;
      } catch {
        return null;
      }
    },
    [ownerKey, currentMonth]
  );

  // initial weekly grid load (for fallback + demo)
  useEffect(() => {
    const controller = new AbortController();
    fetchGrid(controller.signal);
    return () => controller.abort();
  }, [fetchGrid]);

  // demo grid changes with month if no real grid was loaded
  useEffect(() => {
    if (!hasRealSaved) {
      setSavedAvailability(generateDemoWeeklyGrid(currentMonth, ownerKey));
    }
  }, [currentMonth, ownerKey, hasRealSaved]);

  // month change: try server monthly; if not, use fallback merge
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      // 1) try server-side monthly
      const serverMap = await fetchMonthlyAvailability(controller.signal);

      if (!cancelled && serverMap) {
        const filteredServerMap = filterAvailabilityMapByNotice(
          serverMap,
          Date.now() + serverOffsetMs
        );

        setAvailableSlots(filteredServerMap);
        return;
      }
      // 2) fallback: ensure month lessons then compute on client
      if (lastFetchKeyRef.current !== monthKey) {
        lastFetchKeyRef.current = monthKey;
        await fetchMonthLessons(controller.signal);
      }
      const map = computeAvailableSlotsForMonth({
        monthDate: currentMonth,
        savedAvailability,
        timeSlots: defaultTimeSlots,
        lessons: monthLessons,
        nowMs: Date.now() + serverOffsetMs,
      });
      if (!cancelled) setAvailableSlots(map); // computed is canonical
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    monthKey,
    currentMonth,
    savedAvailability,
    monthLessons,
    fetchMonthLessons,
    fetchMonthlyAvailability,
    serverOffsetMs,
  ]);

  // month controls
  const prevMonth = useCallback(() => {
    setCurrentMonth((m) => {
      const d = new Date(m);
      d.setMonth(d.getMonth() - 1);
      return d;
    });
  }, []);
  const nextMonth = useCallback(() => {
    setCurrentMonth((m) => {
      const d = new Date(m);
      d.setMonth(d.getMonth() + 1);
      return d;
    });
  }, []);

  const getDaysInMonth = useCallback(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysCount = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysCount }, (_, i) => new Date(year, month, i + 1));
  }, [currentMonth]);

  const getFirstDayOffset = useCallback(() => {
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Monday-first UI layout
  }, [currentMonth]);

  const hasAvailableSlots = useCallback(
    (date) => {
      const dateStr = formatDateStr(date);
      const slots = availableSlots[dateStr];
      return Array.isArray(slots) && slots.length > 0;
    },
    [availableSlots]
  );

  // on-demand (still uses fallback logic if needed)
  const getAvailableSlotsForMonth = useCallback(
    (monthDate) => {
      return computeAvailableSlotsForMonth({
        monthDate,
        savedAvailability: hasRealSaved
          ? savedAvailability
          : generateDemoWeeklyGrid(monthDate, ownerKey),
        timeSlots: defaultTimeSlots,
        lessons: monthLessons,
        nowMs: Date.now() + serverOffsetMs,
      });
    },
    [savedAvailability, monthLessons, hasRealSaved, ownerKey, serverOffsetMs]
  );

  const reserveSlots = useCallback((dateStr, times) => {
    if (!dateStr || !Array.isArray(times) || times.length === 0) return;
    setAvailableSlots((prev) => {
      const cur = normalizeTimeArray(prev[dateStr] || []);
      const toRemove = new Set(times.map(canonHourStr));
      const next = cur.filter((t) => !toRemove.has(t));
      return { ...prev, [dateStr]: next };
    });
  }, []);

  // ✅ FIXED: always add released times back (works for server-month map too)
  const releaseSlots = useCallback((dateStr, times) => {
    if (!dateStr || !Array.isArray(times) || times.length === 0) return;

    setAvailableSlots((prev) => {
      const cur = normalizeTimeArray(prev[dateStr] || []);
      const curSet = new Set(cur);

      times.map(canonHourStr).forEach((t) => curSet.add(t));

      const sorted = Array.from(curSet).sort((a, b) => a.localeCompare(b));
      return { ...prev, [dateStr]: sorted };
    });
  }, []);

  const value = useMemo(
    () => ({
      daysOfWeek: defaultDaysOfWeek,
      timeSlots: defaultTimeSlots,
      ownerKey,

      savedAvailability,
      hasRealSaved,

      currentMonth,
      setCurrentMonth,
      prevMonth,
      nextMonth,
      getDaysInMonth,
      getFirstDayOffset,

      availableSlots,
      hasAvailableSlots,

      getAvailableSlotsForMonth,
      refresh: () => {},

      reserveSlots,
      releaseSlots,
    }),
    [
      savedAvailability,
      hasRealSaved,
      currentMonth,
      prevMonth,
      nextMonth,
      getDaysInMonth,
      getFirstDayOffset,
      availableSlots,
      hasAvailableSlots,
      getAvailableSlotsForMonth,
      ownerKey,
      reserveSlots,
      releaseSlots,
    ]
  );

  if (typeof window !== "undefined" && !CLEAN_ENV_BASE) {
    console.warn(
      `[TeacherAvailability] API base not set. Falling back to ${FALLBACK_BASE}. ` +
        `Set VITE_API_BASE / REACT_APP_API_BASE for development.`
    );
  }

  return (
    <TeacherAvailabilityContext.Provider value={value}>
      {children}
    </TeacherAvailabilityContext.Provider>
  );
};

export const useTeacherAvailability = () => {
  const ctx = useContext(TeacherAvailabilityContext);
  if (!ctx)
    throw new Error(
      "useTeacherAvailability must be used within a TeacherAvailabilityProvider"
    );
  return ctx;
};